// React Context for user content system
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { UserContentType, UserContentState } from './types';
import { fetchUserContent, USER_CONTENT_PAGE_SIZE } from './api';
import { useUserContext } from '../../context/UserContext';
import { DateTime } from 'luxon';

interface UserContentPagedState extends UserContentState {
  hasMore: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  initialized: boolean;
  skip: number;
  lastFetchedAt: number | null;
}

interface UserContentContextValue extends UserContentState {
  loadUserContent: (type: UserContentType, userId: string, isOwner?: boolean) => Promise<void>;
  loadMoreUserContent: (type: UserContentType, userId: string, isOwner?: boolean) => Promise<void>;
  refreshUserContent: (type: UserContentType, userId: string, isOwner?: boolean) => Promise<void>;
  clearUserContentCache: (userId?: string) => void;
  getUserContentState: (type: UserContentType, userId: string, isOwner?: boolean) => UserContentPagedState;
}

const USER_CONTENT_CACHE_TTL_MS = 3 * 60 * 1000;

const defaultPagedState: UserContentPagedState = {
  items: [],
  loading: false,
  error: undefined,
  hasMore: true,
  loadingMore: false,
  refreshing: false,
  initialized: false,
  skip: 0,
  lastFetchedAt: null,
};

const contentStateKey = (type: UserContentType, userId: string, isOwner = false) => `${userId}:${type}:${isOwner ? 'owner' : 'viewer'}`;

const UserContentContext = createContext<UserContentContextValue>({
  items: [],
  loading: false,
  error: undefined,
  loadUserContent: async () => {},
  loadMoreUserContent: async () => {},
  refreshUserContent: async () => {},
  clearUserContentCache: () => {},
  getUserContentState: () => defaultPagedState,
});

export const UserContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useUserContext();
  const [contentMap, setContentMap] = useState<Record<string, UserContentPagedState>>({});
  const contentMapRef = useRef<Record<string, UserContentPagedState>>({});
  const lastPrincipalRef = useRef<string | null>(currentUser?._id ?? null);

  useEffect(() => {
    contentMapRef.current = contentMap;
  }, [contentMap]);

  // Invalidate all cached content when auth principal changes or logs out.
  useEffect(() => {
    const nextPrincipal = currentUser?._id ?? null;
    if (nextPrincipal !== lastPrincipalRef.current) {
      setContentMap({});
      contentMapRef.current = {};
      lastPrincipalRef.current = nextPrincipal;
    }
  }, [currentUser?._id]);

  const getUserContentState = useCallback(
    (type: UserContentType, userId: string, isOwner = false): UserContentPagedState => {
      return contentMap[contentStateKey(type, userId, isOwner)] ?? defaultPagedState;
    },
    [contentMap],
  );

  const loadUserContent = useCallback(async (type: UserContentType, userId: string, isOwner = false) => {
    const key = contentStateKey(type, userId, isOwner);
    const existing = contentMapRef.current[key];
    const cacheAge = existing?.lastFetchedAt ? DateTime.utc().toMillis() - existing.lastFetchedAt : Number.POSITIVE_INFINITY;
    const cacheFresh = cacheAge < USER_CONTENT_CACHE_TTL_MS;

    if (existing?.initialized && cacheFresh) {
      return;
    }

    setContentMap((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? defaultPagedState),
        // Keep stale data visible when revalidating an expired cache entry.
        loading: !(prev[key]?.items.length ?? 0),
        refreshing: !!(prev[key]?.items.length ?? 0),
        error: undefined,
      },
    }));

    try {
      const data = await fetchUserContent(type, userId, isOwner, { skip: 0 });
      setContentMap((prev) => ({
        ...prev,
        [key]: {
          items: data,
          loading: false,
          error: undefined,
          hasMore: data.length >= USER_CONTENT_PAGE_SIZE,
          loadingMore: false,
          refreshing: false,
          initialized: true,
          skip: data.length,
          lastFetchedAt: DateTime.utc().toMillis(),
        },
      }));
    } catch {
      setContentMap((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] ?? defaultPagedState),
          loading: false,
          refreshing: false,
          error: 'Failed to load user content',
        },
      }));
    }
  }, []);

  const loadMoreUserContent = useCallback(async (type: UserContentType, userId: string, isOwner = false) => {
    const key = contentStateKey(type, userId, isOwner);
    const existing = contentMapRef.current[key] ?? defaultPagedState;

    if (existing.loading || existing.loadingMore || existing.refreshing || !existing.hasMore) {
      return;
    }

    setContentMap((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? defaultPagedState),
        loadingMore: true,
        error: undefined,
      },
    }));

    try {
      const nextItems = await fetchUserContent(type, userId, isOwner, { skip: existing.skip });
      setContentMap((prev) => {
        const previous = prev[key] ?? defaultPagedState;
        const deduped = [...previous.items];
        const existingIds = new Set(previous.items.map((item) => `${item.type}:${item.id}`));
        for (const next of nextItems) {
          const id = `${next.type}:${next.id}`;
          if (!existingIds.has(id)) {
            deduped.push(next);
            existingIds.add(id);
          }
        }

        return {
          ...prev,
          [key]: {
            ...previous,
            items: deduped,
            loading: false,
            loadingMore: false,
            refreshing: false,
            hasMore: nextItems.length >= USER_CONTENT_PAGE_SIZE,
            skip: previous.skip + nextItems.length,
            lastFetchedAt: DateTime.utc().toMillis(),
          },
        };
      });
    } catch {
      setContentMap((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] ?? defaultPagedState),
          loadingMore: false,
          refreshing: false,
          error: 'Failed to load more content',
        },
      }));
    }
  }, []);

  /**
   * Force refresh the first page for a specific user/content-type cache entry.
   * This bypasses the initialized guard used by loadUserContent.
   */
  const refreshUserContent = useCallback(async (type: UserContentType, userId: string, isOwner = false) => {
    const key = contentStateKey(type, userId, isOwner);

    setContentMap((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? defaultPagedState),
        loading: false,
        refreshing: true,
        error: undefined,
      },
    }));

    try {
      const data = await fetchUserContent(type, userId, isOwner, { skip: 0 });
      setContentMap((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] ?? defaultPagedState),
          items: data,
          loading: false,
          loadingMore: false,
          refreshing: false,
          initialized: true,
          hasMore: data.length >= USER_CONTENT_PAGE_SIZE,
          skip: data.length,
          lastFetchedAt: DateTime.utc().toMillis(),
          error: undefined,
        },
      }));
    } catch {
      setContentMap((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] ?? defaultPagedState),
          loading: false,
          loadingMore: false,
          refreshing: false,
          error: 'Failed to refresh user content',
        },
      }));
    }
  }, []);

  /**
   * Clear all cached content, or only entries for a specific profile user.
   */
  const clearUserContentCache = useCallback((userId?: string) => {
    if (!userId) {
      setContentMap({});
      contentMapRef.current = {};
      return;
    }

    setContentMap((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith(`${userId}:`)));
      contentMapRef.current = next;
      return next;
    });
  }, []);

  const aggregated: UserContentState = Object.values(contentMap).find((state) => state.loading || state.loadingMore)
    ?? defaultPagedState;

  return (
    <UserContentContext.Provider
      value={{
        items: aggregated.items,
        loading: aggregated.loading,
        error: aggregated.error,
        loadUserContent,
        loadMoreUserContent,
        refreshUserContent,
        clearUserContentCache,
        getUserContentState,
      }}
    >
      {children}
    </UserContentContext.Provider>
  );
};

export const useUserContent = () => useContext(UserContentContext);
